import { inject, Inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  map,
  mergeMap,
  of,
  tap,
  withLatestFrom,
  zip,
  switchMap,
  retry,
  EMPTY,
  Observable,
} from 'rxjs'
import { SharedImagesApiService } from './shared-images-api.service'
import { ImagesResponse } from './shared-images.models'
import * as SharedImagesActions from './shared-images.actions'
import { GbImageUploadDialogComponent } from 'libs/shared/images/ui/src/lib/gb-image-upload-dialog/gb-image-upload-dialog.component'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { GbImageUploadService } from '@galabid/shared/images/utils'
import { GbBaseDialogService } from '@galabid/shared/utils/dialogs'
import { SharedAuctionFacade } from 'libs/shared/models/auction/auction.models'
import { ImagesEntity } from 'libs/shared/models/images/image.models'
import { HttpErrorResponse } from '@angular/common/http'

@Injectable()
export class SharedImagesEffects {
  protected readonly actions$ = inject(Actions)
  private readonly sharedImagesApiService = inject(SharedImagesApiService)
  private readonly gbDialogService = inject(GbBaseDialogService)
  protected readonly gbImageUploadService = inject(GbImageUploadService)
  private readonly gbErrorMessageService = inject(GbErrorMessageService)
  private readonly gbNotificationService = inject(GbNotificationService)

  openImageUploadDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedImagesActions.openImageUploadDialog),
      exhaustMap((action) => {
        return zip([
          of(action),
          this.gbDialogService.getDialogObs(GbImageUploadDialogComponent, {
            disableClose: true,
            width: '600px',
            mobileFullScreen: true,
            data: { images: action.images, files: action.files, config: action.config },
          }),
        ])
      }),
      withLatestFrom(this.gbImageUploadService.updatedImages$),
      switchMap(([[action, result], images]) => {
        const actions: Action[] = []

        if (result) {
          // check if onSuccess returns an Action to dispatch otherwise execute if returns void
          const onSuccessResult = action.onSuccess?.(images)
          if (onSuccessResult) actions.push(onSuccessResult as Action)
        } else {
          action.onClose?.()
        }

        return actions
      }),
    ),
  )

  uploadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedImagesActions.uploadImage),
      withLatestFrom(this.auctionFacade.urlName$),
      tap(() => this.gbImageUploadService.setWaitingOnResponse(true)),
      concatMap(([action, urlName]) =>
        this.sharedImagesApiService.uploadImage(urlName, action.image).pipe(
          mergeMap((response: ImagesResponse) => this.handleUploadSuccess(response, action.isMulti)),
          catchError((error) => this.handleUploadFailure(error)),
        ),
      ),
    ),
  )

  getImageEntityAndDispatchOnSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedImagesActions.getImageResults),
        filter(() => this.gbImageUploadService.imageUploaderActive),
        mergeMap((action) =>
          this.sharedImagesApiService.getImageEntity(action.imageUuid).pipe(
            map((res) => {
              // trigger a retry if the image isn't ready yet
              if (res.status !== 'success') {
                throw new Error('UPLOAD_PENDING')
              }
              return res
            }),
            // poll API every 3s; after 10 attempts (30s) triggers catchError
            retry({ delay: 3000, count: 10 }),
            map((response) => {
              this.gbImageUploadService.removeFromLoadingList(action.imageUuid)
              const image: ImagesEntity = JSON.parse(response.results as string)
              this.gbImageUploadService.addImageSubject$.next({
                image: image,
                order: null,
              })
            }),
            catchError(() => {
              this.gbImageUploadService.removeFromLoadingList(action.imageUuid)
              this.gbNotificationService.error('SHARED.IMAGE_UPLOADER.UPLOAD_FAILED')
              return EMPTY
            }),
          ),
        ),
      ),
    { dispatch: false },
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SharedImagesActions.uploadImageFailure),
        tap((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          this.gbNotificationService.error(error || 'GENERIC.ERROR')
        }),
      ),
    { dispatch: false },
  )

  constructor(@Inject('AuctionFacade') public auctionFacade: SharedAuctionFacade) {}

  protected handleUploadSuccess<T extends { uuid: string }>(response: T, isMulti?: boolean): Action[] {
    if (!isMulti) {
      this.gbImageUploadService.clearImages()
    }

    const successActions: Action[] = [SharedImagesActions.uploadImageSuccess()]

    if (this.gbImageUploadService.imageUploaderActive) {
      successActions.push(SharedImagesActions.getImageResults({ imageUuid: response.uuid }))
      this.gbImageUploadService.addToLoadingList(response.uuid)
    }

    this.gbImageUploadService.setWaitingOnResponse(false)
    return successActions
  }

  protected handleUploadFailure(error: HttpErrorResponse): Observable<Action> {
    this.gbImageUploadService.setWaitingOnResponse(false)
    return of(SharedImagesActions.uploadImageFailure({ error }))
  }
}
